import { colors } from "Phaser/config";
import GraphicObject from "./GraphicObject";
import { getNoteLengthInMs, NoteLength } from "./Note";
import IPlayable from "./Playable";
import { ProgressState, Result } from "./Progress";
import { getColorByCbType } from "Components/Colors";
import { COLORBLIND_TYPE, ERROR_REC_CLASS } from "Utils/Constants";

export default class Rest extends GraphicObject implements IPlayable {
  isRest = true;
  idx: number;
  length: NoteLength;
  tempo: number;
  graphic: Phaser.GameObjects.Image;
  scale: number = 1;
  isFocused = false;
  timingState?: ProgressState;
  shakeHandle?: Phaser.Tweens.Tween;
  tweenHandle?: Phaser.Tweens.Tween;
  resetColorTimer?: Phaser.Time.TimerEvent;
  cbType?: COLORBLIND_TYPE;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    length: NoteLength,
    tempo: number,
    idx: number,
    hidden?: boolean,
    cbType?: COLORBLIND_TYPE,
  ) {
    super(scene, x, y, "image");
    this.idx = idx;
    this.length = length;
    this.tempo = tempo;
    const [lengthBase] = length.split("-");
    const texture = `${lengthBase}_rest`;
    this.graphic = this.scene.add.image(x, y, texture);
    this.graphic.setOrigin(0.5, 1);
    this.graphic.setY(y + this.graphic.displayHeight / 2.5);
    if (hidden) {
      this.graphic.setAlpha(0);
    }
  }

  getHaloOffset() {
    return 0;
  }

  pulse() {}

  setScale(scale: number) {
    this.graphic.setScale(scale);
    this.scale = scale;
  }

  destroy() {
    this.isFocused = false;
    this.graphic.destroy();
  }

  shake(duration: number): void {
    const pos = this.graphic.x;
    const dpr = 1;
    this.shakeHandle = this.scene.tweens.add({
      targets: { value: 0 },
      value: Math.PI * 2,
      duration,
      ease: Phaser.Math.Easing.Sine.In,
      repeat: 1,
      onUpdate: (_tween, _target, _key, current) => {
        this.graphic.setX(pos + Math.sin(current) * 10 * dpr);
      },
    });
  }

  setFocus(focus: boolean) {
    this.isFocused = focus;
    this.shakeHandle?.remove();
    if (focus) {
      this.graphic.setAlpha(1);
      this.tweenHandle?.stop();
      this.tweenHandle = this.scene.tweens.add({
        targets: this.graphic,
        scale: this.scale * 1.15,
        duration: 100,
        yoyo: true,
        ease: "Power1",
      });
    } else {
      this.graphic?.setScale(this.scale);
      this.tweenHandle?.stop();
    }
  }

  reset(forceColor?: boolean) {
    this.setFocus(false);
    if (forceColor) {
      if (this.resetColorTimer) {
        this.resetColorTimer.remove();
        this.resetColorTimer = undefined;
      }
      this.setColor(colors.fg);
    } else {
      this.resetColorTimer = this.scene.time.delayedCall(
        2500,
        () => this.setColor(colors.fg),
        [],
        this,
      );
    }
  }

  setColor(color: number | string) {
    const col = Phaser.Display.Color.ValueToColor(color).color;
    this.graphic.setTintFill(col);
    this.draw();
  }

  getDurationMs(): number {
    return getNoteLengthInMs(this.length, this.tempo);
  }

  getHeight() {
    return this.graphic?.displayHeight;
  }

  fadeIn(duration: number, delay: number, onDone?: () => void, ctx?: any) {
    this.graphic!.alpha = 0;
    this.scene.tweens.add({
      targets: this.graphic,
      alpha: 1,
      duration,
      delay,
      ease: "Power1",
      onComplete: onDone,
      callbackScope: ctx,
    });
  }

  setAlpha(alpha: number) {
    this.graphic?.setAlpha(alpha);
  }

  getPosition() {
    return {
      x: this.x,
      y: this.y,
    };
  }

  getState() {
    return this.timingState;
  }

  setTimingState(state: ProgressState) {
    console.debug("setting rest state", state);
    this.resetColorTimer?.remove();
    this.timingState = state;
    switch (state) {
      case ProgressState.Missed:
        break;
      default:
        this.setColor(
          getColorByCbType(ERROR_REC_CLASS.SEMI_ERROR, this.cbType ?? null),
        );
        break;
    }
  }

  getResult() {
    return this.timingState === ProgressState.Missed ||
      this.timingState === undefined
      ? Result.Success
      : Result.OffTime;
  }

  getWidth() {
    return this.graphic.displayWidth;
  }
}
